import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CheckCircle, XCircle, Clock, ArrowLeft, AlertTriangle, CheckSquare, FileText, Plus, ShieldCheck, HardHat } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';

/**
 * Parse suggestion text into structured updates
 * Format: "Task Name | Add Hazard: X | Add Control: Y | Add PPE: Z"
 */
const parseSuggestionText = (text) => {
  if (!text) return null;
  
  const result = {
    taskName: '',
    hazards: [],
    controls: [],
    ppe: []
  };
  
  // Split by pipe character
  const parts = text.split('|').map(p => p.trim());
  
  for (const part of parts) {
    if (part.toLowerCase().startsWith('add hazard:')) {
      const value = part.replace(/add hazard:/i, '').trim();
      if (value && !value.toLowerCase().includes('not applicable')) {
        result.hazards.push(value);
      }
    } else if (part.toLowerCase().startsWith('add control:')) {
      const value = part.replace(/add control:/i, '').trim();
      if (value && !value.toLowerCase().includes('not applicable')) {
        result.controls.push(value);
      }
    } else if (part.toLowerCase().startsWith('add ppe:')) {
      const value = part.replace(/add ppe:/i, '').trim();
      if (value && !value.toLowerCase().includes('not applicable')) {
        result.ppe.push(value);
      }
    } else if (!part.toLowerCase().startsWith('task:')) {
      // First non-tagged part is usually the task/category name
      if (!result.taskName && part.length > 0) {
        result.taskName = part;
      }
    } else {
      result.taskName = part.replace(/task:/i, '').trim();
    }
  }
  
  return result;
};

const JSAUpdateQueue = ({ user }) => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [queueItems, setQueueItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  
  // Only supervisors and division admins can access and approve JSA updates
  const canAccess = user?.role === 'supervisor' || user?.role === 'division_admin' || user?.role === 'company_admin';
  
  useEffect(() => {
    if (!canAccess) {
      toast.error('Access denied - Only supervisors can approve JSA updates');
      navigate('/');
    }
  }, [user, navigate, canAccess]);
  
  useEffect(() => {
    const loadQueue = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(`/jsa-update-queue?status=${statusFilter}`);
        setQueueItems(response.data.queue || []);
      } catch (error) {
        console.error('Failed to load JSA update queue:', error);
        toast.error('Failed to load JSA updates');
      } finally {
        setLoading(false);
      }
    };
    loadQueue();
  }, [statusFilter]);

  const refreshQueue = async () => {
    try {
      const response = await axiosInstance.get(`/jsa-update-queue?status=${statusFilter}`);
      setQueueItems(response.data.queue || []);
    } catch (error) {
      console.error('Failed to refresh queue:', error);
    }
  };
  
  const handleApprove = async (queueId, suggestionIndex) => {
    try {
      await axiosInstance.post(`/jsa-update-queue/${queueId}/approve/${suggestionIndex}`);
      toast.success('✅ JSA update approved! The actual JSA has been updated.', {
        duration: 4000
      });
      refreshQueue();
    } catch (error) {
      console.error('Failed to approve:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve update');
    }
  };
  
  const handleReject = async (queueId, suggestionIndex) => {
    try {
      await axiosInstance.post(`/jsa-update-queue/${queueId}/reject/${suggestionIndex}`);
      toast.success('❌ JSA update rejected and removed from queue.', {
        duration: 3000
      });
      refreshQueue();
    } catch (error) {
      console.error('Failed to reject:', error);
      toast.error('Failed to reject update');
    }
  };
  
  const getApprovalLevelBadge = (level) => {
    if (level === 'supervisor') return <Badge className="bg-blue-500">Supervisor</Badge>;
    if (level === 'company_admin') return <Badge className="bg-purple-500">Company Admin</Badge>;
    if (level === 'super_admin') return <Badge className="bg-red-500">Super Admin</Badge>;
    return <Badge>Unknown</Badge>;
  };
  
  const getStatusBadge = (status) => {
    if (status === 'pending') return <Badge variant="outline" className="text-orange-600"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    if (status === 'rejected') return <Badge variant="outline" className="text-red-600"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
    if (status.startsWith('approved_')) return <Badge variant="outline" className="text-green-600"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
    return <Badge>{status}</Badge>;
  };
  
  const canApprove = (requiredLevel) => {
    if (user?.role === 'super_admin') return true;
    if (user?.role === 'company_admin' && (requiredLevel === 'supervisor' || requiredLevel === 'company_admin')) return true;
    if (user?.role === 'supervisor' && requiredLevel === 'supervisor') return true;
    return false;
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading JSA updates...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
              <CheckSquare className="w-6 sm:w-8 h-6 sm:h-8 text-orange-600" />
              JSA Update Queue
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">
              Review and approve AI-suggested JSA updates from safety audits
            </p>
          </div>
        </div>
        
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-900">How This Works</p>
              <p className="text-sm text-blue-700 mt-1">
                Safety audits generate suggested updates for your <strong>actual JSAs</strong>. 
                When you approve an update, the hazards, controls, and PPE will be added directly to the linked JSA document.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Status Filter Tabs */}
      <Tabs value={statusFilter} onValueChange={setStatusFilter} className="mb-6">
        <TabsList>
          <TabsTrigger value="pending">
            Pending Approval
            {queueItems.length > 0 && (
              <Badge className="ml-2 bg-orange-500">{queueItems.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="applied">Applied</TabsTrigger>
        </TabsList>
      </Tabs>
      
      {/* Info Banner */}
      {statusFilter === 'pending' && queueItems.length > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-3">
            <p className="text-sm text-blue-800">
              💡 <strong>Tip:</strong> When you approve or reject an item, it will move from this Pending view. 
              Approved items can be found in the <strong>Applied</strong> tab.
            </p>
          </CardContent>
        </Card>
      )}
      
      {/* Queue Items */}
      <div className="space-y-4">
        {queueItems.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <CheckSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-semibold">No {statusFilter} JSA updates</p>
              <p className="text-sm mt-2">
                {statusFilter === 'pending' 
                  ? 'JSA update suggestions will appear here when audits are completed.' 
                  : 'Applied updates will show here.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          queueItems.map((queue) => (
            <Card key={queue.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">
                      Audit: {queue.location}
                    </CardTitle>
                    <CardDescription>
                      {new Date(queue.audit_date).toLocaleDateString()} • Supervisor: {queue.supervisor}
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="text-sm">
                    {queue.suggestions?.length || 0} Suggestion(s)
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {queue.suggestions?.map((suggestion, idx) => {
                    // Parse the suggestion text to get structured data
                    const parsed = parseSuggestionText(suggestion.suggestion_text);
                    const hasChanges = parsed && (parsed.hazards.length > 0 || parsed.controls.length > 0 || parsed.ppe.length > 0);
                    
                    return (
                    <div key={idx} className="border-2 border-gray-200 rounded-xl p-5 bg-white shadow-sm">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4 pb-3 border-b">
                        <div className="flex items-center gap-3">
                          <div className="bg-orange-100 p-2 rounded-lg">
                            <FileText className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900">
                              {parsed?.taskName || suggestion.parsed_data?.task || 'JSA Update'}
                            </h4>
                            <p className="text-xs text-gray-500">Suggestion #{idx + 1}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(suggestion.status)}
                        </div>
                      </div>
                      
                      {/* WHAT'S BEING UPDATED - Clear Visual Section */}
                      <div className="mb-4">
                        <h5 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                          <Plus className="w-4 h-4" />
                          PROPOSED CHANGES:
                        </h5>
                        
                        {hasChanges ? (
                          <div className="space-y-3">
                            {/* Hazards to Add */}
                            {parsed.hazards.length > 0 && (
                              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <AlertTriangle className="w-4 h-4 text-red-600" />
                                  <span className="font-semibold text-red-800 text-sm">Hazards to Add</span>
                                </div>
                                <ul className="space-y-1">
                                  {parsed.hazards.map((h, i) => (
                                    <li key={i} className="text-sm text-red-900 flex items-start gap-2">
                                      <span className="text-red-500 font-bold">+</span>
                                      {h}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {/* Controls to Add */}
                            {parsed.controls.length > 0 && (
                              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <ShieldCheck className="w-4 h-4 text-green-600" />
                                  <span className="font-semibold text-green-800 text-sm">Safety Controls to Add</span>
                                </div>
                                <ul className="space-y-1">
                                  {parsed.controls.map((c, i) => (
                                    <li key={i} className="text-sm text-green-900 flex items-start gap-2">
                                      <span className="text-green-500 font-bold">+</span>
                                      {c}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {/* PPE to Add */}
                            {parsed.ppe.length > 0 && (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <HardHat className="w-4 h-4 text-blue-600" />
                                  <span className="font-semibold text-blue-800 text-sm">PPE to Add</span>
                                </div>
                                <ul className="space-y-1">
                                  {parsed.ppe.map((p, i) => (
                                    <li key={i} className="text-sm text-blue-900 flex items-start gap-2">
                                      <span className="text-blue-500 font-bold">+</span>
                                      {p}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        ) : (
                          /* Fallback - show raw suggestion if parsing fails */
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">
                              {suggestion.suggestion_text || "No details available"}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {/* Source Info */}
                      <div className="text-xs text-gray-500 mb-4 p-2 bg-gray-50 rounded-lg flex items-center gap-2">
                        <FileText className="w-3 h-3" />
                        <span>From audit at <span className="font-semibold text-gray-700">{queue.location}</span> on {new Date(queue.audit_date).toLocaleDateString()}</span>
                      </div>
                      
                      {/* Action Buttons */}
                      {suggestion.status === 'pending' && (
                        <div className="flex gap-3 pt-3 border-t">
                          {canApprove(suggestion.required_approval_level) ? (
                            <>
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700 flex-1"
                                onClick={() => handleApprove(queue.id, idx)}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Approve Changes
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50 flex-1"
                                onClick={() => handleReject(queue.id, idx)}
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Reject
                              </Button>
                            </>
                          ) : (
                            <Badge variant="outline" className="text-orange-600 py-2 px-3">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Requires {suggestion.required_approval_level} approval
                            </Badge>
                          )}
                        </div>
                      )}
                      
                      {/* Approval Info */}
                      {suggestion.approved_by && (
                        <div className="mt-3 pt-3 border-t text-xs text-gray-500 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          Approved on {new Date(suggestion.approved_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  );})}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default JSAUpdateQueue;
